import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-[#0A1128] border border-[#d4af37]/25 px-4 py-3.5 text-slate-50 placeholder:text-slate-400 focus:outline-none focus:border-[#d4af37] transition-colors duration-300";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data =
        mode === "login"
          ? await login(form.email, form.password)
          : await register(form.name, form.email, form.password);
      toast.success(
        mode === "login"
          ? `Welcome back, ${data.name.split(" ")[0]}!`
          : "Account created — welcome aboard!"
      );
      navigate(data.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-32 pb-20" data-testid="auth-page">
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="auth-back-home"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-[#d4af37] transition-colors mb-8"
        >
          <ArrowLeft size={14} /> Back to home
        </Link>
        <div className="backdrop-blur-xl bg-[#101C3E]/60 border border-[#d4af37]/25 p-8 md:p-10">
          <h1 className="font-display text-3xl md:text-4xl font-light text-slate-50 mb-2">
            {mode === "login" ? (
              <>Welcome <span className="italic text-[#d4af37]">back.</span></>
            ) : (
              <>Create your <span className="italic text-[#d4af37]">account.</span></>
            )}
          </h1>
          <p className="text-sm text-slate-400 mb-8">
            {mode === "login"
              ? "Sign in to view your trips and manage bookings."
              : "Save your enquiries and track your journeys in one place."}
          </p>

          <div className="grid grid-cols-2 border border-[#d4af37]/25 mb-8">
            <button
              data-testid="auth-tab-login"
              onClick={() => { setMode("login"); setError(""); }}
              className={`py-3 text-xs tracking-[0.2em] uppercase transition-colors ${mode === "login" ? "bg-[#d4af37] text-[#0A1128] font-medium" : "text-slate-400 hover:text-[#d4af37]"}`}
            >
              Sign In
            </button>
            <button
              data-testid="auth-tab-register"
              onClick={() => { setMode("register"); setError(""); }}
              className={`py-3 text-xs tracking-[0.2em] uppercase transition-colors ${mode === "register" ? "bg-[#d4af37] text-[#0A1128] font-medium" : "text-slate-400 hover:text-[#d4af37]"}`}
            >
              Register
            </button>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <input
                data-testid="auth-name-input"
                className={inputCls}
                placeholder="Your full name"
                value={form.name}
                onChange={set("name")}
                required
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              className={inputCls}
              placeholder="Email address"
              value={form.email}
              onChange={set("email")}
              required
            />
            <input
              data-testid="auth-password-input"
              type="password"
              className={inputCls}
              placeholder={mode === "register" ? "Password (min 6 characters)" : "Password"}
              value={form.password}
              onChange={set("password")}
              required
            />
            {error && (
              <p data-testid="auth-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 px-4 py-3">
                {error}
              </p>
            )}
            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 bg-[#d4af37] text-[#0A1128] px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300 hover:bg-[#f3e5ab] disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
